import { NetworkErrorService } from '@app/services/NetworkError.service';
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { PropertyGroupApi } from '@app/services/apiServices/PropertyGroupApi';
import { PropertyApi } from '@app/services/apiServices/PropertyApi';
import { SpinnerService } from '@app/services/spinner.service';
import { UserStore } from '@app/services/stores/UserStore';
import { PropertyGroup } from "@app/models/PropertyGroup";
import { PropertyGroupCandidate } from "@app/models/PropertyGroupCandidate";
import { Property } from "@app/models/Property";
import { take } from "rxjs/operators";
import { Component, Inject, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import { DialogOkayCancelComponent } from "@app/components/sharedDialogs/dialogOkayCancel.component";
import { OneButtonDialogComponent } from "@app/components/sharedDialogs/oneButtonDialog.component";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: 'app-property-group-details',
    template: require('./propertygroupdetails.html')
})
export class PropertyGroupDetailsComponent implements OnInit{

    propertyGroupId: number;
    propertyGroup: PropertyGroup;
    staticPropertyGroupNickname: string;
    staticPmcCouponId: string;
    staticPmcName: string;
    disableInputsAddress: boolean = true;
    groupCandidates: PropertyGroupCandidate[];
    sortedGroupCandidates: PropertyGroupCandidate[];
    groupMembers: Property[];
    sortedGroupMembers: Property[];
    invalidCheckVar: boolean = false;
    propertyGroupNicknameError: boolean = false;
    nickname: string;
    doneLoading: boolean = false;
    nicknameChanged: boolean = false;

    constructor(@Inject(MatDialog) private dialog: MatDialog,
                @Inject(PropertyGroupApi) private propertyGroupApi: PropertyGroupApi,
                @Inject(PropertyApi) private propertyApi: PropertyApi,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
                @Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(UserStore) private userStore: UserStore,
                @Inject(Router) private router: Router,
                @Inject(ActivatedRoute) private route: ActivatedRoute) {

    }

    ngOnInit() {

        if (this.route.snapshot.params.propertyGroupId) {
            this.spinnerService.showSpinner();
            this.propertyGroupId = this.route.snapshot.params.propertyGroupId;
            this.staticPropertyGroupNickname = "";
            this.getPropertyGroupDetails().then(() => {
                this.spinnerService.closeSpinner();
                this.doneLoading = true;
            }, () => {
                this.cancel();
            });
        }

    }

    getPropertyGroupDetails() {
        return Promise.all([
            this.loadPropertyGroup(),
            this.loadPropertyGroupCandidates(),
            this.loadGroupMembers()
        ]);
    }

    loadPropertyGroup(): Promise<void> {

        return new Promise<void>((resolve, reject) => {
            this.propertyGroupApi.propertyGroupIdGet(this.propertyGroupId)
                .pipe(take(1)).subscribe(resp => {
                    this.propertyGroup = resp;
                    this.nickname = this.propertyGroup.nickname;
                    this.staticPropertyGroupNickname = this.propertyGroup.nickname;
                    resolve();
                }, (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe(resp => {
                            if (resp === true) {
                                this.loadPropertyGroup();
                            }
                            reject();
                        });
                });
        });
    }

    loadPropertyGroupCandidates(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            let userId = this.userStore.getHoaHomeownerId();
            this.propertyGroupApi.getPropertyGroupCandidates(userId, this.propertyGroupId)
                .pipe(take(1)).subscribe((candidateResp) => {
                    this.groupCandidates = candidateResp;
                    this.sortGroupCandidates();
                    resolve();
                }, (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.loadPropertyGroupCandidates();
                            }
                            reject();
                        });
                });
        });
    }

    loadGroupMembers(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.propertyGroupApi.getPropertyGroupMembers(this.propertyGroupId)
                .pipe(take(1)).subscribe((memberResp) => {
                    this.groupMembers = memberResp;
                    this.staticPmcCouponId = this.groupMembers[0].hoa.pmc.couponId;
                    this.staticPmcName = this.groupMembers[0].hoa.pmc.name;
                    this.sortGroupMembers();
                    resolve();
                }, (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.loadGroupMembers();
                            }
                            reject();
                        });
                });
        });
    }

    rename() {
        this.spinnerService.showSpinner();

        this.propertyGroupApi.propertyGroupRename(this.propertyGroupId, this.nickname)
            .pipe(take(1)).subscribe(() => {
                this.getPropertyGroupDetails().then(() => this.spinnerService.closeSpinner());
            },  (err) => {
                const dialogRef = this.dialog.open(DialogErrorDialog);
                dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.rename();
                            } else {
                                this.getPropertyGroupDetails().then(() => this.spinnerService.closeSpinner());
                            }
                        });
                })
            });
    }

    removeProperty(propertyId: number) {
        this.spinnerService.showSpinner();
        let dialogRef = this.dialog.open(DialogOkayCancelComponent, {
            data: {
                'header': 'Release Managed Account',
                'message': 'This will remove the currently registered property from the managed accounts group.',
                'message2': 'It will not remove the property or any of the payment schedules or payment history associated with it.',
                'message3': 'If removing this property leaves only one property in the group, then the managed accounts group will also be deleted.'
            }
        });
        dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
            if (value === 'OK') {
                this.propertyGroupApi.propertyGroupRemoveProperties(this.propertyGroupId, propertyId)
                    .pipe(take(1)).subscribe((resp) => {
                        if (resp.status === 200) {
                            this.propertyGroupApi.propertyGroupIdGet(this.propertyGroupId)
                                .pipe(take(1)).subscribe(() => {
                                    this.getPropertyGroupDetails().then(() => this.spinnerService.closeSpinner());
                                }, () => {
                                    this.spinnerService.closeSpinner();
                                    const propertyIdParam = this.getLastPropertyId(propertyId);
                                    this.router.navigateByUrl('/properties/propertyGroup/create/' + propertyIdParam,
                                        { state:{ propertyId: propertyIdParam } });
                                });

                        } else {
                            this.getPropertyGroupDetails().then(() => this.spinnerService.closeSpinner());
                        }
                    }, (err) => {

                        let dialogRef = this.dialog.open(DialogErrorDialog);
                        dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                            this.networkErrorService.handleNetworkError(err, true)
                                .pipe(take(1)).subscribe((resp) => {
                                    if (resp === true) {
                                        this.removeProperty(propertyId);
                                    } else {
                                        this.getPropertyGroupDetails().then(() => this.spinnerService.closeSpinner());
                                    }
                                });
                        })
                    });
            }
            else {
                this.spinnerService.closeSpinner();
            }
        });
    }

    addProperty(registeredPropertyId: number) {
        this.spinnerService.showSpinner();
        const propertyIds: number[] = [registeredPropertyId];
        this.propertyGroupApi.propertyGroupAddProperties(this.propertyGroupId, propertyIds)
            .pipe(take(1)).subscribe(() => {
                this.getPropertyGroupDetails().then(() => this.spinnerService.closeSpinner());
            }, (err) => {
                let dialogRef = this.dialog.open(DialogErrorDialog);
                dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.addProperty(registeredPropertyId);
                            } else {
                                this.getPropertyGroupDetails().then(() => this.spinnerService.closeSpinner());
                            }
                        });
                })
            });
    }

    getLastPropertyId(deletedPropertyId: number) {
        let lastPropertyId = undefined;
        if(this.groupMembers.length === 2) {
            if(this.groupMembers[0].id === deletedPropertyId) {
                lastPropertyId = this.groupMembers[1].id;
            } else {
                lastPropertyId = this.groupMembers[0].id;
            }
        }
        return lastPropertyId;
    }

    register(groupCandidate: PropertyGroupCandidate) {
        this.spinnerService.showSpinner();

        this.propertyGroupApi.getHoa(groupCandidate.couponData)
            .pipe(take(1)).subscribe((resp) => {
                if(resp.status === 200) {
                    let hoa = resp.body;

                    const newProperty: Property = {
                        couponId: groupCandidate.couponData.propertyCouponId,
                        hoa: hoa,
                        address: this.groupMembers[0].address,
                        nickname: groupCandidate.nickname
                    }
                    let userId = this.userStore.getHoaHomeownerId();

                    this.propertyApi.homeownersIdPropertiesPost(false, userId, newProperty)
                        .pipe(take(1)).subscribe({ next: (response) => {
                            this.addProperty(response.body.id);
                        }, error: err => {
                            let errorMessage = err.error.message;
                            if (err.error.errors && err.error.errors.length > 0) {
                                err.error.errors.forEach(msg => {
                                    errorMessage += '<br><br>' + msg;
                                })
                            }
                            this.errorHandlerService.handleNetworkError(err, true, errorMessage)
                                .pipe().subscribe(() =>  {
                                    this.spinnerService.closeSpinner()
                                })
                        } });
                }
            },  (err) => {

                const dialogRef = this.dialog.open(DialogErrorDialog);
                dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.register(groupCandidate);
                            } else {
                                this.getPropertyGroupDetails().then(() => this.spinnerService.closeSpinner());
                            }
                        });
                })
            });

    }

    cancel() {
        this.router.navigateByUrl('/properties');
    }

    invalid() {
        return !this.nicknameChanged;
    }

    propertyGroupNickname() {
        this.propertyGroup.nickname = this.nickname;
        this.nicknameChanged = true;
        if (this.propertyGroup.nickname === undefined) {
            this.propertyGroupNicknameError = true;
        } else {
            this.propertyGroupNicknameError = !this.propertyGroup.nickname;
        }
    }

    defaultNickname() {
        if (!this.nicknameChanged)
            this.nickname = this.propertyGroup.nickname;
    }

    addPropertyHelper() {
        this.dialog.open(OneButtonDialogComponent,{
            data: {
                "header": "Add Account",
                "message": "Add the currently registered property to the group",
                "buttonText": "Close"
            }
        });
    }

    groupNameHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Group Nickname",
                "message": "Provide a name for the related accounts group.  This will be the name displayed on the home page that can be expanded to show the accounts in the group.",
                "buttonText": "Close"
            }
        });
    }

    registerPropertyHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Register Account",
                "message": "Registers the property and adds it to the group",
                "buttonText": "Close"
            }
        });
    }

    removePropertyHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Remove Account",
                "message": "Removes the currently registered property from the group",
                "buttonText": "Close"
            }
        });
    }

    private sortGroupMembers() {
        this.sortedGroupMembers = [];

        if (!this.groupMembers || this.groupMembers.length === 0) {
            return;
        } else if (this.groupMembers.length === 1) {
            this.sortedGroupMembers.push(this.groupMembers[0])
        }

        this.sortedGroupMembers = this.groupMembers.sort((a,b) => b.id - a.id)
    }

    private sortGroupCandidates() {
        this.sortedGroupCandidates = [];

        if (!this.groupCandidates || this.groupCandidates.length === 0) {
            return;
        } else if (this.groupCandidates.length === 1) {
            this.sortedGroupCandidates.push(this.groupCandidates[0])
            return
        }

        // orderBy: propertyId, couponData.hoaCouponId, couponData.propertyCouponId
        this.sortedGroupCandidates = this.groupCandidates.sort((a, b) => {

            let returnValue = b.propertyId - a.propertyId;

            if (returnValue === 0) {

                if (a.couponData.hoaCouponId < b.couponData.hoaCouponId) {
                    returnValue = -1
                } else if (a.couponData.hoaCouponId > b.couponData.hoaCouponId) {
                    returnValue = 1
                }
            }

            if (returnValue === 0) {
                if (a.couponData.propertyCouponId < b.couponData.propertyCouponId) {
                    returnValue = -1
                } else if (a.couponData.propertyCouponId > b.couponData.propertyCouponId) {
                    returnValue = 1
                }
            }

            return returnValue;
        })

    }
}
